import { Alert, AlertIcon, Button, Icon, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiExternalLink } from "react-icons/fi";
import { Link as RouterLink } from "react-router-dom";
import { MarketDialogueStatus, type ProcurementCommentRoundDto } from "../../../../../../autogen/bff-api";
import { useDisplayDateWithTime } from "../../../../../../common/formatting/useDisplayDateWithTime";
import { urls } from "../../../../../../urls";
import { useCommentRoundLoad, useCommentRounds } from "./CommentRoundContext";

export const CommentRoundsNotice = () => {
  const { t } = useTranslation();
  const rounds = useCommentRounds();
  const load = useCommentRoundLoad();
  const displayDateWithTime = useDisplayDateWithTime();
  if (load.failed) {
    return (
      <Alert status="warning" borderRadius="md" mt="4" alignItems="flex-start" data-testid="comment-rounds-failed">
        <AlertIcon />
        <Stack spacing="2" flex="1" minW="0" fontSize="sm">
          <Text>
            {t(
              "The comment rounds could not be loaded, so the suppliers' comments and «Send out for comment» are not shown.",
            )}
          </Text>
          <Button
            size="sm"
            variant="outline"
            alignSelf="flex-start"
            onClick={load.retry}
            isLoading={load.isRetrying}
          >
            {t("Try again")}
          </Button>
        </Stack>
      </Alert>
    );
  }
  if (!rounds?.rounds?.length) return null;

  const describe = (round: ProcurementCommentRoundDto) => {
    switch (round.status) {
      case MarketDialogueStatus.Draft:
      case MarketDialogueStatus.PublicationFailed:
        return t("Round {{number}} is being prepared and has not been sent to suppliers yet.", {
          number: round.number,
        });
      case MarketDialogueStatus.PublicationPending:
        return t("Round {{number}} is waiting to be published on Doffin.", { number: round.number });
      case MarketDialogueStatus.Published:
        return round.answerDeadline
          ? t("Round {{number}} is open for comments until {{deadline}}.", {
              number: round.number,
              deadline: displayDateWithTime(round.answerDeadline),
            })
          : t("Round {{number}} is open for comments.", { number: round.number });
      case MarketDialogueStatus.AnswerDeadlinePassed:
        return t(
          "Round {{number}} has closed. The suppliers' comments are shown beside each requirement, criterion, document and price form.",
          { number: round.number },
        );
    }
  };

  return (
    <Alert status="info" borderRadius="md" mt="4" alignItems="flex-start" data-testid="comment-rounds-notice">
      <AlertIcon />
      <Stack spacing="2" flex="1" minW="0" fontSize="sm">
        <Text fontWeight="semibold">{t("Sent out for comment")}</Text>
        {rounds.rounds.map((round) => (
          <Stack key={round.dialogueId} spacing="1">
            <Text>{describe(round)}</Text>
            <Button
              as={RouterLink}
              to={urls.app.marketDialogues.view.go(round.dialogueId)}
              target="_blank"
              variant="link"
              size="sm"
              alignSelf="flex-start"
              colorScheme="teal"
              rightIcon={<Icon as={FiExternalLink} />}
            >
              {t("Open round {{number}}", { number: round.number })}
            </Button>
          </Stack>
        ))}
      </Stack>
    </Alert>
  );
};
